/**
 * OrganizationExport - Pagina Export Dati GDPR
 * Il Diario di Bordo - SaaS
 * 
 * Permette al Responsabile di:
 * - Richiedere un export completo dei dati (GDPR Art. 20)
 * - Visualizzare lo stato degli export in corso
 * - Scaricare export completati
 * - Vedere lo storico degli export precedenti
 * 
 * @version 1.0.0
 * @created Febbraio 2025
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi } from '../../services/organizationApi'
import {
  Download,
  Loader2,
  FileArchive,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Shield,
  ArrowLeft,
  FileText,
  Users,
  Baby,
  Building2,
  XCircle,
  Info
} from 'lucide-react'
import { Link } from 'react-router-dom'

// ============================================
// HELPERS
// ============================================

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/D'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/D'
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const exportTypeLabels = {
  organization_full: 'Organizzazione completa',
  tenant_full: 'Centro completo',
  minore_gdpr: 'Dati minore (GDPR)',
  pre_deletion_org: 'Backup pre-cancellazione',
  pre_deletion_tenant: 'Backup pre-cancellazione centro'
}

const statusConfig = {
  pending: { 
    label: 'In coda', 
    color: 'text-yellow-600 dark:text-yellow-400', 
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    icon: Clock 
  },
  processing: { 
    label: 'In elaborazione', 
    color: 'text-blue-600 dark:text-blue-400', 
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    icon: Loader2 
  },
  completed: { 
    label: 'Completato', 
    color: 'text-green-600 dark:text-green-400', 
    bg: 'bg-green-50 dark:bg-green-900/20',
    icon: CheckCircle 
  },
  failed: { 
    label: 'Errore', 
    color: 'text-red-600 dark:text-red-400', 
    bg: 'bg-red-50 dark:bg-red-900/20',
    icon: XCircle 
  },
  expired: { 
    label: 'Scaduto', 
    color: 'text-gray-500 dark:text-gray-400', 
    bg: 'bg-gray-50 dark:bg-gray-800',
    icon: Clock 
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function OrganizationExport() {
  const { user } = useAuth()
  const { success, error: notifyError } = useNotification()
  
  const [loading, setLoading] = useState(true)
  const [requesting, setRequesting] = useState(false)
  const [downloading, setDownloading] = useState(null)
  const [exports, setExports] = useState([])

  // Carica lista export
  const loadExports = useCallback(async () => {
    try {
      const res = await organizationApi.getExports()
      setExports(res.data?.exports || [])
    } catch (err) {
      console.error('Errore caricamento export:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadExports()
  }, [loadExports])

  // Auto-refresh se ci sono export in corso
  useEffect(() => {
    const hasPending = exports.some(e => e.status === 'pending' || e.status === 'processing')
    if (!hasPending) return

    const interval = setInterval(loadExports, 10000) // ogni 10s
    return () => clearInterval(interval)
  }, [exports, loadExports])

  // Richiedi nuovo export
  const handleRequestExport = async () => {
    // Verifica se c'è già un export in corso
    const inProgress = exports.find(e => e.status === 'pending' || e.status === 'processing')
    if (inProgress) {
      notifyError('C\'è già un export in corso. Attendi il completamento.')
      return
    }

    try {
      setRequesting(true)
      await organizationApi.requestDataExport()
      success('Export avviato! Riceverai un\'email quando sarà pronto per il download.')
      await loadExports()
    } catch (err) {
      const msg = err.response?.data?.message || 'Errore durante la richiesta di export'
      notifyError(msg)
    } finally {
      setRequesting(false)
    }
  }

  // Download export
  const handleDownload = async (exportId) => {
    try {
      setDownloading(exportId)
      const res = await organizationApi.downloadExport(exportId)
      
      // Crea blob e scarica
      const blob = new Blob([res.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `export-dati-${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      success('Download avviato!')
    } catch (err) {
      notifyError('Errore durante il download. Il file potrebbe essere scaduto.')
    } finally {
      setDownloading(null)
    }
  }

  // Stato: export in corso?
  const activeExport = exports.find(e => e.status === 'pending' || e.status === 'processing')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <Link
          to="/organization"
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center gap-1 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna alla Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Export Dati
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Scarica una copia completa dei dati della tua organizzazione (GDPR Art. 20)
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
        <div className="flex gap-3">
          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
              Portabilità dei dati
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              L'export genera un archivio ZIP contenente tutti i dati in formato JSON, 
              standard aperto e machine-readable come richiesto dal GDPR. 
              Il file include: dati organizzazione, centri, anagrafica minori, registrazioni e utenti. 
              I dati sensibili (password, token) vengono automaticamente rimossi.
            </p>
          </div>
        </div>
      </div>

      {/* Contenuto export */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Contenuto dell'archivio
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <Building2 className="h-5 w-5 text-primary-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Organizzazione</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Dati e impostazioni</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <Baby className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Minori</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Anagrafica completa</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <FileText className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Registrazioni</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Diario e presenze</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <Users className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Utenti</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Educatori e staff</p>
            </div>
          </div>
        </div>
      </div>

      {/* Azione: Richiedi export */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Nuovo Export
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {activeExport 
                ? 'Un export è attualmente in elaborazione. Riceverai un\'email quando sarà pronto.'
                : 'L\'elaborazione avviene in background. Riceverai un\'email con il link per il download.'
              }
            </p>
          </div>
          
          <button
            onClick={handleRequestExport}
            disabled={requesting || !!activeExport}
            className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {requesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Richiesta in corso...
              </>
            ) : activeExport ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Export in corso...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Richiedi Export
              </>
            )}
          </button>
        </div>

        {/* Progress bar export attivo */}
        {activeExport && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {activeExport.status === 'pending' ? 'In coda...' : 'Elaborazione in corso...'}
              </span>
              {activeExport.progress > 0 && (
                <span className="text-sm text-blue-600 dark:text-blue-400 ml-auto">
                  {activeExport.progress}%
                </span>
              )}
            </div>
            {activeExport.progress > 0 && (
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${activeExport.progress}%` }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Storico Export */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Storico Export
          </h2>
          <button
            onClick={loadExports}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Aggiorna"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        
        {exports.length === 0 ? (
          <div className="p-8 text-center">
            <FileArchive className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              Nessun export effettuato
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Richiedi il tuo primo export per scaricare una copia dei dati
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {exports.map((exp) => {
              const config = statusConfig[exp.status] || statusConfig.pending
              const StatusIcon = config.icon
              const isDownloadable = exp.status === 'completed' && exp.downloadAvailable
              const isExpired = exp.status === 'completed' && !exp.downloadAvailable
              
              return (
                <div key={exp.id} className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {exportTypeLabels[exp.type] || exp.type}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                          <StatusIcon className={`h-3 w-3 ${exp.status === 'processing' ? 'animate-spin' : ''}`} />
                          {isExpired ? 'Download scaduto' : config.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span>{formatDate(exp.createdAt)}</span>
                        {exp.fileSize && (
                          <>
                            <span>·</span>
                            <span>{formatFileSize(exp.fileSize)}</span>
                          </>
                        )}
                        {exp.downloadExpiresAt && exp.downloadAvailable && (
                          <>
                            <span>·</span>
                            <span className="text-orange-500">
                              Scade: {formatDate(exp.downloadExpiresAt)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Download button */}
                    {isDownloadable && (
                      <button
                        onClick={() => handleDownload(exp.id)}
                        disabled={downloading === exp.id}
                        className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
                      >
                        {downloading === exp.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Scaricando...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Scarica ZIP
                          </>
                        )}
                      </button>
                    )}
                    
                    {isExpired && (
                      <span className="text-sm text-gray-400 dark:text-gray-500 italic">
                        Link scaduto
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Note GDPR */}
      <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <Info className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
          <p>
            Il link per il download è valido per <strong>48 ore</strong>. 
            Dopo la scadenza sarà necessario richiedere un nuovo export.
          </p>
          <p>
            I file vengono conservati per 7 giorni e poi eliminati automaticamente dal server.
          </p>
        </div>
      </div>
    </div>
  )
}
